/*!
 * @license
 * Alfresco Example Content Application
 *
 * Copyright (C) 2005 - 2020 Alfresco Software Limited
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail.  Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * along with Alfresco. If not, see <http://www.gnu.org/licenses/>.
 */

import { ProfileState, RuleContext } from '@alfresco/adf-extensions';
import { GROUP_RECORDS_SITE_COLLABORATOR, GROUP_RECORDS_SITE_CONTRIBUTOR, GROUP_RECORDS_SITE_MANAGER, UDEU_DIGITAL_ASPECT, UDEU_RECORD_DRAFT_ASPECT, UDEU_RECORD_TYPE, UDEU_RECORD_LOANS_UUIDS_PROP, UDEU_FILE_ASPECT, BORROWED_RECORD_ASPECT, SITE_TYPE } from '../model/contants/deusto';
import { NodeEntry } from '@alfresco/js-api';
import { hasAspect, isAllowedOperation, isInGroup, isType, isTypeViewer } from 'projects/alden-extension/src/lib/rules/alden.rules';
import { CREATE_ALLOWABLE_OPERATION, DELETE_ALLOWABLE_OPERATION, UPDATE_ALLOWABLE_OPERATION } from 'projects/alden-extension/src/lib/models/alden-constants';
import { canAssignRegisterToLoan } from './loan.rules';

export function isRecordsViewer(context: RuleContext): boolean {
  return isTypeViewer(context, 'records');
}

export function canCreateRecords(context: RuleContext): boolean {
  const profile = context.profile;
  return isInGroup(profile, GROUP_RECORDS_SITE_MANAGER) || isInGroup(profile, GROUP_RECORDS_SITE_COLLABORATOR) || isInGroup(profile, GROUP_RECORDS_SITE_CONTRIBUTOR);
}

export function canViewRecord(context: RuleContext): boolean {
  const selection = context.selection;
  const nodes = selection.nodes;
  if(selection.count === 1 && isType(nodes[0], UDEU_RECORD_TYPE)) {
    return true;
  }
  return false; 
}

export function canEditRecord(context: RuleContext): boolean {
  const selection = context.selection;
  const nodes = selection.nodes;
  if(selection.count === 1 && recordAllowedOperation(nodes[0], UPDATE_ALLOWABLE_OPERATION)) {
    return true;
  }
  return false; 
}

export function canDuplicateRecord(context: RuleContext): boolean {
  const selection = context.selection;
  const nodes = selection.nodes;
  if(selection.count === 1 && recordAllowedOperation(nodes[0], CREATE_ALLOWABLE_OPERATION)) {
    return true;
  }
  return false; 
}

export function canDeleteRecord(context: RuleContext): boolean {
  const profile = context.profile;
  const selection = context.selection;
  const nodes = selection.nodes;

  if(selection.count > 0 && (isInGroup(profile, GROUP_RECORDS_SITE_MANAGER) || isInGroup(profile, GROUP_RECORDS_SITE_COLLABORATOR))
      && nodes.every((node) => recordAllowedOperation(node, DELETE_ALLOWABLE_OPERATION) && !hasAspect(node, BORROWED_RECORD_ASPECT))) {
    return true;
  }
  return false;
}

export function canEditMassiveRecord(context: RuleContext): boolean {
  const profile = context.profile;
  const selection = context.selection;
  const nodes = selection.nodes;

  if(selection.count > 1 && (isInGroup(profile, GROUP_RECORDS_SITE_MANAGER) || isInGroup(profile, GROUP_RECORDS_SITE_COLLABORATOR))
      && nodes.every((node) => recordAllowedOperation(node, UPDATE_ALLOWABLE_OPERATION))) {
    return true;
  }

  return false;
}

export function canAssignLoan(context: RuleContext): boolean {
  const selection = context.selection;
  const nodes = selection.nodes;
  if(canAssignRegisterToLoan(context) && selection.count > 0 && !nodes.some((node) => !isType(node, UDEU_RECORD_TYPE) || hasAspect(node, UDEU_RECORD_DRAFT_ASPECT))) {
    const lents: string[] = nodes[0].entry.properties[UDEU_RECORD_LOANS_UUIDS_PROP];
    if((lents?.length > 0) && nodes.some((node) => !hasAspect(node, UDEU_DIGITAL_ASPECT))) {
      return false;
    }
    return true;
  }
  return false;
}

export function canExportRegisterFile(context: RuleContext): boolean {
  const selection = context.selection;
  const nodes = selection.nodes;
  return nodes.every((node) => !hasAspect(node, UDEU_FILE_ASPECT) || !isARecordFile(node) || isAllowedOperation(node, UPDATE_ALLOWABLE_OPERATION) || isADigitalRecordFile(node));
}

export function canSearchByCreator(profile: ProfileState): boolean{
  return isInGroup(profile, GROUP_RECORDS_SITE_MANAGER);
}

function recordAllowedOperation(node: NodeEntry, allowableOperation: string): boolean {
  return isType(node, UDEU_RECORD_TYPE) && isAllowedOperation(node, allowableOperation);
}

function isARecordFile(node: NodeEntry) {
  return node.entry.path.elements.some((element) => element.nodeType === SITE_TYPE && element.name === "records");
}

function isADigitalRecordFile(node: NodeEntry) {
  return node.entry.path.elements.some((element) => element.nodeType === UDEU_RECORD_TYPE && element.aspectNames?.includes(UDEU_DIGITAL_ASPECT));
}